import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import {
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  FormControl,
  Input as MuiInput,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import styled from "styled-components";
import { FontStyles } from "../../index";
import { fonts, Spacing } from "../../../config";

const StyledInput = styled(MuiInput)(
  ({ theme, error, $minHeight, $inputStyle }: any) => ({
    "&&.MuiInput-root": {
      ...FontStyles.medium14,
      padding: theme.spacing(1.962, 2.5),
      alignItems: $minHeight && "flex-start",
      paddingTop: $minHeight && Spacing(2.5),
      marginTop: Spacing(1.5),
      textOverflow: "ellipsis",
      color: error ? "red" : theme.palette.text.primary,
      backgroundColor: "#E9E9E9",
      borderBottom: "0px",
      borderRadius: theme.spacing(0.6),
      "& .MuiInputBase-input": {
        padding: 0,
        backgroundColor: "transparent",
        boxShadow: "transparent 0px 0px !important",
        fontSize: fonts.sizes.small,
      },
      ...$inputStyle,
    },
  })
);

const StyledMenuItem = styled(MenuItem)(({ theme }: any) => ({
  ...FontStyles.medium14,
  backgroundColor: "#E9E9E9",
  "&.Mui-focusVisible": { backgroundColor: "transparent" },
  "&.Mui-selected": {
    backgroundColor: "#E9E9E9 !important",
    borderRadius: "21px",
    color: "#272727 !important",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "#F5F5F5 !important",
    borderRadius: "21px",
  },
  "&:hover": {
    backgroundColor: "#F5F5F5 !important",
    borderRadius: "21px",
    color: "#272727 !important",
  },
  "&:focus": {
    backgroundColor: "#E9E9E9 !important",
    borderRadius: "21px",
  },
  "&.Mui-selected:focus": {
    backgroundColor: "#F5F5F5 !important",
    borderRadius: "21px",
  },
}));

const StyledSelect = styled(MuiSelect)(
  ({ theme, $withError, disabled }: any) => {
    const { palette: colors, spacing } = theme;
    return {
      ...FontStyles.medium14,
      color: colors.text.primary,
      padding: spacing(1),
      backgroundColor: "#E9E9E9",
      borderRadius: 21,
      marginTop: "2vh",
      opacity: disabled ? 0.6 : 1,
      border: $withError
        ? `1px solid ${colors.background.secondary}`
        : "0px solid red",
      "& .MuiSelect-select.MuiSelect-select": {
        paddingLeft: spacing(1),
        backgroundColor: "#E9E9E9",
      },
      "& .MuiSelect-icon": {
        color: "#272727",
        paddingRight: spacing(0.5),
      },
    };
  }
);

const StyledFormControl = styled(FormControl)(() => ({}));

const Label = styled.p(({ theme, $withError, white, $padding }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "capitalize",
    padding: $padding || '5px',
    margin: 0,
    color: $withError
      ? colors.error.main
      : white
        ? "white"
        : colors.action.selected,
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
  };
});

export interface SelectRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  options: Array<{ label: string; value: string | number }>;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: RegisterOptions;
  onChangeCustom?: (value: any) => void;
  labelStyles?: any;
  defaultValue?: any;
  white?: boolean;
}

const MENU_PROPS = {
  PaperProps: {
    style: {
      backgroundColor: "#E9E9E9",
      borderRadius: "21px",
    },
  },
  MenuListProps: {
    style: { margin: "0 5px 0 5px" },
  },
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
};

function SelectRHF({
  control,
  name,
  label,
  options,
  disabled,
  readOnly,
  rules,
  onChangeCustom,
  labelStyles,
  defaultValue,
  white,
}: SelectRHFProps) {
  const formContext = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}

      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        return (
          <StyledFormControl
            fullWidth
            error={Boolean(errorMsg)}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <Label
              $padding={labelStyles}
              white={white}
              $withError={Boolean(errorMsg)}
            >
              {label}
            </Label>
            <StyledSelect
              white={white}
              readOnly={readOnly}
              disabled={disabled}
              $withError={Boolean(errorMsg)}
              disableUnderline
              value={field.value ?? ""}
              onChange={(e) => {
                field.onChange(e.target.value);
                if (onChangeCustom) onChangeCustom(e.target.value);
                if (fieldState.invalid) formContext?.trigger(name);
              }}
              onBlur={() => {
                field.onBlur();
                formContext?.trigger(name);
              }}
              onMouseUp={(e) => e.stopPropagation()}
              IconComponent={ExpandMore}
              MenuProps={MENU_PROPS}
            >
              {options?.map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  {option.label}
                </StyledMenuItem>
              ))}
            </StyledSelect>
            <FormHelperText>{errorMsg ? errorMsg : " "}</FormHelperText>
          </StyledFormControl>
        );
      }}
    />
  );
}

export default SelectRHF;
